import React, { ReactNode, ReactElement, FC, JSX } from 'react';
import { cn } from '@shadcn/lib/utils';

const applyStrongClass = (node: ReactNode): ReactNode => {
	if (typeof node === 'string' || typeof node === 'number') {
		return node;
	}

	if (React.isValidElement(node)) {
		if (node.type === 'strong') {
			return React.cloneElement(
				node as ReactElement,
				{
					// @ts-expect-error TODO
					className: cn((node as ReactElement).props.className, 'font-bold'),
				} as React.HTMLAttributes<HTMLElement>,
			);
		}

		return React.cloneElement(
			node as ReactElement,
			{
				children: React.Children.map(
					// @ts-expect-error TODO
					(node as ReactElement).props.children,
					applyStrongClass,
				),
			} as React.HTMLAttributes<HTMLElement>,
		);
	}

	return node;
};

const hasStrongTag = (node: ReactNode): boolean => {
	if (typeof node === 'string' || typeof node === 'number') {
		return false;
	}

	if (React.isValidElement(node)) {
		if (node.type === 'strong') {
			return true;
		}

		// @ts-expect-error TODO
		return React.Children.toArray((node as ReactElement).props.children).some(hasStrongTag);
	}

	return false;
};

export interface TypographyProps {
	as?: keyof JSX.IntrinsicElements;
	bold?: boolean;
	className?: string;
	children: ReactNode;
	useOnlyStyles?: boolean;
}

const withBoldLogic = (
	Component: React.FC<TypographyProps>,
	defaultClassName: string,
	tag: keyof JSX.IntrinsicElements,
): React.FC<TypographyProps> => {
	const WithBoldLogic = ({
		children,
		className,
		bold,
		useOnlyStyles,
		...props
	}: TypographyProps) => {
		const modifiedChildren = React.Children.map(children, applyStrongClass);
		const isBold =
			bold ||
			defaultClassName.includes('font-bold') ||
			React.Children.toArray(modifiedChildren).some(hasStrongTag);

		const { ...restProps } = props;

		if (useOnlyStyles && React.isValidElement(children)) {
			return React.cloneElement(children, {
				...restProps,
				// @ts-expect-error TODO
				className: cn(defaultClassName, className, children.props.className, {
					'font-bold': isBold,
					'font-light': !isBold && defaultClassName.includes('font-light'),
					'font-normal': !isBold && !defaultClassName.includes('font-light'),
					'font-medium': !isBold && defaultClassName.includes('font-medium'),
				}),
			});
		}

		return (
			<Component
				as={tag}
				className={cn(defaultClassName, className, {
					'font-bold': isBold,
					'font-light': !isBold && defaultClassName.includes('font-light'),
					'font-normal': !isBold && !defaultClassName.includes('font-light'),
					'font-medium': !isBold && defaultClassName.includes('font-medium'),
				})}
				{...restProps}
			>
				{modifiedChildren}
			</Component>
		);
	};
	return WithBoldLogic;
};

const Typography: React.FC<TypographyProps> = ({
	as: Component = 'p',
	className,
	children,
	useOnlyStyles,
	...props
}) => {
	if (useOnlyStyles && React.isValidElement(children)) {
		return React.cloneElement(children as ReactElement, {
			...props,
			// @ts-expect-error TODO
			className: cn(className, (children as ReactElement).props.className),
		});
	}

	return (
		<Component {...props} className={className}>
			{children}
		</Component>
	);
};

export const H1 = withBoldLogic(
	Typography,
	'text-[64px] leading-[72px] tracking-[-1.28px] font-light',
	'h1',
);
export const H2 = withBoldLogic(Typography, 'text-5xl leading-[56px] font-light', 'h2');
export const H3 = withBoldLogic(Typography, 'text-[40px] leading-[48px] font-light', 'h3');
export const H4 = withBoldLogic(Typography, 'text-2xl leading-10 font-normal', 'h4');
export const H5 = withBoldLogic(
	Typography,
	'text-sm font-bold uppercase leading-[14px] tracking-[1.12px]',
	'h5',
);
export const Large = withBoldLogic(Typography, 'text-lg leading-10 font-normal', 'p');
export const Regular = withBoldLogic(Typography, 'text-base leading-8 font-normal', 'p');
export const Small = withBoldLogic(Typography, 'text-sm leading-6 font-medium', 'p');
export const Disclaimer = withBoldLogic(Typography, 'text-sm font-medium italic leading-5', 'p');

export default Typography;
